import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';
import moment from 'moment';

import {EmailTemplate} from 'nutshell-core/settings-constants';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Dialog} from 'shells/dialog/dialog';
import {DisabledSection} from 'shells/disabled-section/disabled-section';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';
import {roundTimeToNextQuarter} from 'shells/select-time-picker/helpers';
import {PlanUpgradeLink} from 'shells/upgrade/plan-upgrade-link';

import type {CampaignType} from 'nutshell-graphql-types';
import type {
    EditionFragment as Edition,
    Setting as SettingType,
    SendEdition as SendEditionMutation,
    SendEditionVariables as SendEditionMutationVariables,
    ScheduleEdition as ScheduleEditionMutation,
    ScheduleEditionVariables as ScheduleEditionMutationVariables,
} from 'nutshell-graphql-types';

import {Setting} from '../../../core/setting';

import {getComparisonDetails} from '../a-b-testing/helpers';
import type {ModalComponent} from '../edition-page';

import SCHEDULE_EMAIL_EDITION from './graphql/mutations/schedule-email-marketing-edition.graphql';
import SEND_EMAIL_EDITION from './graphql/mutations/send-email-marketing-edition.graphql';
import {PrepareToSendView, MIN_AUDIENCE_SIZE} from './prepare-to-send-view';
import {PreviewView} from './preview-view';
import {ScheduleView} from './schedule-view';

import styles from './prepare-to-send-modal.module.css';

type ViewType = 'prepare' | 'schedule' | 'preview';

type CurrentView = {
    view: ViewType;
    scheduling: boolean;
};

type OwnProps = {
    isOpen: boolean;
    onClose: (
        newView?: string | null | undefined,
        onboardingModalComponent?: ModalComponent
    ) => void;
    edition: Edition;
    isEditionLoading: boolean;
    isScheduling: boolean;
    onSendSuccess: () => void;
    type: CampaignType;
};

type Props = OwnProps & {
    onSend: () => Promise<any>;
    onSchedule: (arg1: moment.Moment) => Promise<any>;
};

function PrepareToSendModalComponent(props: Props) {
    const businessHoursStart = moment(
        NutshellFrontendSettings.businessHours.start,
        NutshellFrontendSettings.businessHours.format
    );
    if (businessHoursStart.isBefore(moment())) {
        businessHoursStart.add(1, 'day');
    }
    const [viewHistory, setViewHistory] = React.useState<CurrentView[]>([
        {view: 'prepare', scheduling: props.isScheduling},
    ]);
    const [isShowingSendConfirmation, setIsShowingSendConfirmation] =
        React.useState<boolean>(false);
    const [sendEmailButtonRef, setSendEmailButtonRef] = React.useState<
        HTMLElement | null | undefined
    >(null);
    const [isSending, setIsSending] = React.useState<boolean>(false);
    const [scheduledDate, setScheduledDate] = React.useState<moment.Moment>(
        roundTimeToNextQuarter(businessHoursStart)
    );

    // @ts-expect-error
    const currentView: CurrentView = viewHistory[viewHistory.length - 1];
    const setCurrentView = (input: {newView?: ViewType; scheduling?: boolean}) => {
        const newView = input.newView ? input.newView : currentView.view;
        const scheduling =
            input.scheduling !== undefined ? input.scheduling : currentView.scheduling;

        setViewHistory(viewHistory.concat({view: newView, scheduling: scheduling}));
    };

    React.useEffect(() => {
        setViewHistory([{view: 'prepare', scheduling: props.isScheduling}]);
    }, [props.isScheduling]);

    const onCloseModal = (editorTab?: string | null, modalComponent?: ModalComponent) => {
        setIsShowingSendConfirmation(false);
        setIsSending(false);
        props.onClose(editorTab, modalComponent);
        setTimeout(() => {
            // @ts-expect-error
            setViewHistory([viewHistory[0]]);
        }, 100);
    };

    const audienceCount = props.edition.audience
        ? props.edition.audience.membershipStats.totalCount.value
        : undefined;
    const audienceUnsubcribedRate = props.edition.audience
        ? props.edition.audience.membershipStats.unsubscribedRate
        : undefined;

    return (
        <Setting settingName={EmailTemplate.STREET_ADDRESS_SETTING_NAME}>
            {({setting}) => {
                const issueCount = getIssueCount(props.edition, setting);

                const errorBanner = props.edition.gateError ? (
                    <div className='mb-16'>
                        <FlashBanner type='warning' animate={false} hasBorder={true}>
                            <div className='full-width flex justify-sb'>
                                {props.edition.gateError.message}
                                <div className={styles['flash-banner-cta-container']}>
                                    <PlanUpgradeLink />
                                </div>
                            </div>
                        </FlashBanner>
                    </div>
                ) : undefined;

                const goToPreviewOrScheduleButtons = (
                    <div>
                        <Button
                            variant='text'
                            size='big'
                            // If the error banner exists, we have a sending
                            // error and need to disable this button
                            // @ts-expect-error
                            disabled={issueCount > 0 || errorBanner || props.isEditionLoading}
                            onClick={() => {
                                setCurrentView({newView: 'schedule', scheduling: true});
                            }}
                        >
                            Schedule for a later date
                        </Button>
                        <Button
                            variant='primary'
                            size='big'
                            onClick={() => {
                                setCurrentView({newView: 'preview'});
                            }}
                            // If the error banner exists, we have a sending
                            // error and need to disable this button
                            disabled={Boolean(
                                issueCount > 0 || errorBanner || props.isEditionLoading
                            )}
                        >
                            Review
                        </Button>
                    </div>
                );

                const goToScheduleButton = (
                    <div>
                        <Button
                            variant='primary'
                            size='big'
                            onClick={() => {
                                setCurrentView({newView: 'schedule'});
                            }}
                            disabled={Boolean(
                                issueCount > 0 || props.isEditionLoading || errorBanner
                            )}
                        >
                            Schedule and review
                        </Button>
                    </div>
                );

                const goToPreviewFromScheduleButton = (
                    <div>
                        <Button
                            variant='primary'
                            size='big'
                            onClick={() => {
                                setCurrentView({newView: 'preview'});
                            }}
                        >
                            Next
                        </Button>
                    </div>
                );

                let buttonText = '';
                if (currentView.scheduling) {
                    buttonText = `Schedule for ${scheduledDate.format('MMMM Do [at] h:mm A')}`;
                } else if (props.edition.isABTesting) {
                    buttonText = 'Send A/B test';
                } else {
                    buttonText = `Send${
                        audienceCount
                            ? ` to ${audienceCount.toLocaleString()} ${
                                  audienceCount > 1 ? 'recipients' : 'recipient'
                              }`
                            : ''
                    }`;
                }

                const sendButton = (
                    <SaveButton
                        type='button'
                        // @ts-expect-error
                        onClick={() => {
                            if (currentView.scheduling) {
                                // Schedule the email
                                setIsSending(true);
                                setIsShowingSendConfirmation(false);
                                props.onSchedule(scheduledDate).then(() => {
                                    setIsSending(false);
                                });
                            } else {
                                setIsShowingSendConfirmation(true);
                            }
                        }}
                        // @ts-expect-error
                        getButtonRef={(ref) => {
                            setSendEmailButtonRef(ref);
                        }}
                        // Ideally we wouldn't actually run into any of these
                        // issues. The audience and from values should be set
                        // when the edition is created, and the user shouldn't
                        // be able to get to the edition preview page with any
                        // issues. But, each of these values _could_ not exist
                        // and that alone is enough of a reason to make sure.
                        disabled={
                            !props.edition.audience ||
                            !props.edition.from ||
                            (props.edition.from && props.edition.from.status !== 'VERIFIED') ||
                            issueCount > 0 ||
                            props.isEditionLoading
                        }
                        buttonText={buttonText}
                        savingText={currentView.scheduling ? 'Scheduling' : 'Sending'}
                        successText={currentView.scheduling ? 'Scheduled!' : 'Sent!'}
                        onClearSaveComplete={() => {
                            onCloseModal();
                        }}
                        isSaving={isSending}
                        saveCompleteTimeout={1000}
                    />
                );

                let footerPrimaryButtons = null;

                if (currentView.view === 'prepare') {
                    if (currentView.scheduling) {
                        footerPrimaryButtons = goToScheduleButton;
                    } else {
                        footerPrimaryButtons = goToPreviewOrScheduleButtons;
                    }
                } else if (currentView.view === 'schedule') {
                    footerPrimaryButtons = goToPreviewFromScheduleButton;
                } else if (currentView.view === 'preview') {
                    footerPrimaryButtons = sendButton;
                }

                let mainContent = <div />;

                if (currentView.view === 'prepare') {
                    mainContent = (
                        <PrepareToSendView
                            edition={props.edition}
                            streetAddress={setting ? setting.value : null}
                            closeAndMoveToView={(
                                editorTab: string,
                                modalComponent?: ModalComponent
                            ) => {
                                onCloseModal(editorTab, modalComponent);
                            }}
                            issueCount={issueCount}
                            type={props.type}
                            isLoading={props.isEditionLoading}
                            audienceCount={audienceCount}
                            audienceUnsubcribedRate={audienceUnsubcribedRate}
                        />
                    );
                } else if (currentView.view === 'schedule') {
                    mainContent = (
                        <ScheduleView
                            scheduledDate={scheduledDate}
                            setScheduledDate={setScheduledDate}
                            onReviewAndSend={() => {
                                // props.setIsScheduling(false);
                                setCurrentView({newView: 'preview', scheduling: false});
                            }}
                            type={props.type}
                        />
                    );
                } else if (currentView.view === 'preview') {
                    mainContent = (
                        <PreviewView
                            edition={props.edition}
                            scheduledDate={currentView.scheduling ? scheduledDate : undefined}
                            type={props.type}
                        />
                    );
                }

                return (
                    <Modal
                        headerText={getModalTitle(issueCount, currentView.scheduling, props.type)}
                        isOpen={props.isOpen}
                        onClose={onCloseModal}
                        preventBodyScroll={true}
                        maxHeight={725}
                    >
                        {errorBanner}
                        <div className={styles.container}>
                            {errorBanner ? (
                                <DisabledSection>{mainContent}</DisabledSection>
                            ) : (
                                mainContent
                            )}
                        </div>
                        <FooterBar
                            left={
                                <Button
                                    variant='text'
                                    size='big'
                                    onClick={() => {
                                        if (viewHistory.length === 1) {
                                            onCloseModal();
                                        } else {
                                            setViewHistory(viewHistory.slice(0, -1));
                                        }
                                    }}
                                >
                                    {currentView.view === 'prepare' ? 'Cancel' : 'Back'}
                                </Button>
                            }
                            right={footerPrimaryButtons}
                        />
                        {isShowingSendConfirmation ? (
                            <Dialog
                                anchor={sendEmailButtonRef}
                                question={
                                    typeof audienceCount === 'number'
                                        ? `This message will be sent to ${audienceCount}
                                           ${audienceCount !== 1 ? 'recipients' : 'recipient'}.
                                            Are you sure you'd like to send this now?`
                                        : // This case shouldn't ever happen - but default fallback message
                                          "This cannot be undone, are you sure you'd like to send this now?"
                                }
                                primaryText='Yes, send now'
                                onPrimary={() => {
                                    setIsSending(true);
                                    setIsShowingSendConfirmation(false);
                                    props.onSend().then(() => {
                                        setIsSending(false);
                                    });
                                }}
                                secondaryText='No, don’t send'
                                onSecondary={() => {
                                    setIsShowingSendConfirmation(false);
                                }}
                            />
                        ) : undefined}
                    </Modal>
                );
            }}
        </Setting>
    );
}

export function PrepareToSendModal(props: OwnProps) {
    const [sendEdition] = useMutation<SendEditionMutation, SendEditionMutationVariables>(
        SEND_EMAIL_EDITION
    );

    const [scheduleEdition] = useMutation<
        ScheduleEditionMutation,
        ScheduleEditionMutationVariables
    >(SCHEDULE_EMAIL_EDITION);

    return (
        <PrepareToSendModalComponent
            {...props}
            onSend={() => {
                return sendEdition({
                    variables: {
                        edition: {editionId: props.edition.id},
                    },
                }).then(() => {
                    props.onSendSuccess();
                });
            }}
            onSchedule={(scheduledTime: moment.Moment) => {
                return scheduleEdition({
                    variables: {
                        edition: {
                            editionId: props.edition.id,
                            scheduledTime: scheduledTime.unix(),
                        },
                    },
                });
            }}
        />
    );
}

function getModalTitle(issueCount: number, isScheduling: boolean, type: CampaignType): string {
    const userFacingNoun = type === 'NEWSLETTER' ? 'edition' : 'broadcast';
    if (isScheduling) {
        return issueCount > 0
            ? `Prepare ${userFacingNoun} for scheduling`
            : 'Schedule for a later date';
    }

    return issueCount > 0 ? `Prepare ${userFacingNoun} for sending` : 'Ready for sending';
}

function getIssueCount(edition: Edition, streetAddressSetting?: SettingType | null): number {
    const comparisonEdition = edition.comparisonEdition;
    const {comparisonSubject} = getComparisonDetails(
        edition,
        comparisonEdition && comparisonEdition.emailTemplate
    );

    let count = 0;
    // @ts-expect-error
    if (!edition.audience || edition.audience.membershipStats.totalCount === 0) {
        count += 1;
    }

    if (
        !edition.emailTemplate ||
        !edition.emailTemplate.subject ||
        (edition.isABTesting && !comparisonSubject)
    ) {
        count += 1;
    }

    if (
        !edition.hasUnsubscribeLink ||
        (edition.isABTesting && comparisonEdition && !comparisonEdition.hasUnsubscribeLink)
    ) {
        count += 1;
    }

    if (
        !edition.hasStreetAddress ||
        !streetAddressSetting ||
        !streetAddressSetting.value ||
        (edition.isABTesting && comparisonEdition && !comparisonEdition.hasStreetAddress)
    ) {
        count += 1;
    }

    if (
        !edition.from ||
        !edition.audience ||
        (edition.from && edition.from.status !== 'VERIFIED')
    ) {
        count += 1;
    }

    if (
        edition.isABTesting &&
        edition.audience &&
        edition.audience.membershipStats.totalCount.value < MIN_AUDIENCE_SIZE
    ) {
        count += 1;
    }

    if (NutClientConfig.enforceDmarcSetup) {
        if (
            !edition.from ||
            !edition.from.fromDomain ||
            edition.from.fromDomain.refreshedDmarcStatus !== 'STATUS_VERIFIED'
        ) {
            count += 1;
        }
    }

    if (edition.customClickDomain && !edition.customClickDomain.isVerified) {
        count += 1;
    }

    return count;
}
